import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent, CheckboxComponent, Item, SelectComponent } from '@zero/shared/ui-web-design-system';
import { TradeFacade } from '@zero/trade/domain';
import { BehaviorSubject, filter, map } from 'rxjs';

@Component({
  selector: 'trade-knowledge-and-experience',
  imports: [TranslocoPipe, SelectComponent, CheckboxComponent, ButtonComponent, AsyncPipe],
  templateUrl: './knowledge-and-experience.component.html',
  styleUrl: './knowledge-and-experience.component.scss',
})
export class KnowledgeAndExperienceComponent implements OnInit {
  public isins = input<string[]>();
  public readonly tradeFacade = inject(TradeFacade);

  readonly #destroyRef = inject(DestroyRef);
  readonly #translocoService = inject(TranslocoService);
  readonly #cTx = new BehaviorSubject<Item[]>([]);
  readonly #lTx = new BehaviorSubject<Item[]>([]);
  readonly #cVol = new BehaviorSubject<Item[]>([]);
  readonly #lVol = new BehaviorSubject<Item[]>([]);
  readonly #cExp = new BehaviorSubject<Item[]>([]);
  readonly #lExp = new BehaviorSubject<Item[]>([]);

  public readonly cTx$ = this.#cTx.asObservable();
  public readonly lTx$ = this.#lTx.asObservable();
  public readonly cVol$ = this.#cVol.asObservable();
  public readonly lVol$ = this.#lVol.asObservable();
  public readonly cExp$ = this.#cExp.asObservable();
  public readonly lExp$ = this.#lExp.asObservable();
  public isCheckboxChecked = false;

  public readonly shouldDisplayForm$ = this.tradeFacade.knowledgeAndExperience$.pipe(map((kae) => !!kae?.showForm));

  ngOnInit(): void {
    this.tradeFacade.loadKnowledgeAndExperienceForm(this.isins());
    this.#setDropdowns();
  }

  public selectCTx = (selected: Item) => this.#cTx.next(this.#getItemsUpdated(selected, this.#cTx.value));
  public selectLTx = (selected: Item) => this.#lTx.next(this.#getItemsUpdated(selected, this.#lTx.value));
  public selectCVol = (selected: Item) => this.#cVol.next(this.#getItemsUpdated(selected, this.#cVol.value));
  public selectLVol = (selected: Item) => this.#lVol.next(this.#getItemsUpdated(selected, this.#lVol.value));
  public selectCExp = (selected: Item) => this.#cExp.next(this.#getItemsUpdated(selected, this.#cExp.value));
  public selectLExp = (selected: Item) => this.#lExp.next(this.#getItemsUpdated(selected, this.#lExp.value));

  public submit = () =>
    this.tradeFacade.submitKnowledgeAndExperienceForm({
      certificateTransactions: this.#getSelectedValue(this.#cTx.value),
      leverageTransactions: this.#getSelectedValue(this.#lTx.value),
      certificateVolume: this.#getSelectedValue(this.#cVol.value),
      leverageVolume: this.#getSelectedValue(this.#lVol.value),
      certificateYearsExperience: this.#getSelectedValue(this.#cExp.value),
      leverageYearsExperience: this.#getSelectedValue(this.#lExp.value),
    });

  readonly #getItemsUpdated = (selected: Item, items: Item[]) => [
    ...items.map((item) => ({ ...item, selected: item.value === selected.value })),
  ];

  readonly #getSelectedValue = (items: Item[]) => {
    const selected = items.find((item) => item.selected)?.value;
    return selected && !Number.isNaN(Number(selected)) ? Number(selected) : null;
  };

  readonly #getTxValues = (transactions: number) => [
    { value: 1, label: '1-9', selected: transactions === 1 },
    { value: 10, label: '10+', selected: transactions === 10 },
    { value: 20, label: '20+', selected: transactions === 20 },
    { value: 50, label: '50+', selected: transactions === 50 },
  ];

  readonly #getVolValues = (volume: number) => [
    { value: 500, label: '500+', selected: volume === 500 },
    { value: 1000, label: '1.000+', selected: volume === 1000 },
    { value: 2000, label: '2.000+', selected: volume === 2000 },
    { value: 5000, label: '5.000+', selected: volume === 5000 },
  ];

  readonly #getExpValues = (experience: number) => [
    { value: 1, label: '1', selected: experience === 1 },
    { value: 2, label: '2', selected: experience === 2 },
    { value: 3, label: '3', selected: experience === 3 },
    { value: 4, label: '3+', selected: experience === 4 },
  ];

  readonly #setDropdowns = () =>
    this.tradeFacade.knowledgeAndExperience$
      .pipe(
        filter((kae) => !!kae),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(
        ({
          certificateTransactions: cTx,
          leverageTransactions: lTx,
          certificateVolume: cVol,
          leverageVolume: lVol,
          certificateYearsExperience: cExp,
          leverageYearsExperience: lExp,
        }) => {
          const none = this.#translocoService.translate('mydepot.trading.knowledge-and-experience.none');
          this.#cTx.next([{ value: 0, label: none, selected: cTx === 0 }, ...this.#getTxValues(cTx)]);
          this.#lTx.next([{ value: 0, label: none, selected: lTx === 0 }, ...this.#getTxValues(lTx)]);
          this.#cVol.next([{ value: 0, label: none, selected: cVol === 0 }, ...this.#getVolValues(cVol)]);
          this.#lVol.next([{ value: 0, label: none, selected: lVol === 0 }, ...this.#getVolValues(lVol)]);
          this.#cExp.next([{ value: 0, label: none, selected: cExp === 0 }, ...this.#getExpValues(cExp)]);
          this.#lExp.next([{ value: 0, label: none, selected: lExp === 0 }, ...this.#getExpValues(lExp)]);
        },
      );
}
